import React, { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { GameButton, PixelIcon, Modal, CurrencyDisplay } from '@/components/ui';
import type { PixelIconName, GameButtonProps } from '@/components/ui';
import { ObstacleTile } from '@/components/game';
import { pixelIcons } from '@/game/pixelIcons';
import { ThemeManager } from '@/services/themeManager';

interface DesignSystemScreenProps {
  onGoHome: () => void;
}

// All theme CSS custom properties we track
const themeVars = [
  { name: '--pixel-bg-primary', label: 'Background Primary' },
  { name: '--pixel-bg-secondary', label: 'Background Secondary' },
  { name: '--pixel-bg-light', label: 'Background Light' },
  { name: '--pixel-text-primary', label: 'Text Primary' },
  { name: '--pixel-text-secondary', label: 'Text Secondary' },
  { name: '--pixel-text-dark', label: 'Text Dark' },
  { name: '--pixel-border', label: 'Border' },
  { name: '--pixel-border-light', label: 'Border Light' },
  { name: '--pixel-primary', label: 'Primary' },
  { name: '--pixel-secondary', label: 'Secondary' },
  { name: '--pixel-success', label: 'Success' },
  { name: '--pixel-danger', label: 'Danger' },
  { name: '--pixel-warning', label: 'Warning' },
  { name: '--pixel-info', label: 'Info' },
  { name: '--pixel-player', label: 'Player' },
  { name: '--pixel-player-shadow', label: 'Player Shadow' },
  { name: '--pixel-glow', label: 'Glow' },
  { name: '--pixel-shadow', label: 'Shadow' },
];

const buttonVariants = ['primary', 'secondary', 'success', 'warning', 'danger', 'info'] as const;
const buttonSizes = ['small', 'medium', 'large'] as const;
const modalVariants = ['default', 'primary', 'secondary', 'success', 'warning', 'danger', 'info'] as const;

// Sections for navigation
const sections = [
  { id: 'colors', label: 'Colors & Themes' },
  { id: 'typography', label: 'Typography' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'icons', label: 'Icons' },
  { id: 'modals', label: 'Modals' },
  { id: 'tiles', label: 'Game Tiles' },
  { id: 'currency', label: 'Currency' },
  { id: 'animations', label: 'Animations' },
  { id: 'spacing', label: 'Spacing' },
] as const;

// Color swatch component
const ColorSwatch: React.FC<{ varName: string; label: string }> = ({ varName, label }) => {
  const computedColor = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  const isTransparent = computedColor.includes('rgba') || computedColor.includes('transparent');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 12px',
        borderRadius: '8px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          background: isTransparent ? computedColor : `var(${varName})`,
          border: '2px solid rgba(255,255,255,0.2)',
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: 'var(--pixel-text-primary)',
            fontSize: '12px',
            fontFamily: "'Press Start 2P', monospace",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
        <code
          style={{
            color: 'var(--pixel-text-secondary)',
            fontSize: '10px',
            display: 'block',
            marginTop: '2px',
          }}
        >
          {varName}
        </code>
        <code
          style={{
            color: 'var(--pixel-info)',
            fontSize: '9px',
            display: 'block',
            marginTop: '1px',
          }}
        >
          {computedColor}
        </code>
      </div>
    </div>
  );
};

// Section header component
const SectionHeader: React.FC<{ id: string; title: string; description?: string }> = ({
  id,
  title,
  description,
}) => (
  <div id={id} style={{ marginBottom: '16px', scrollMarginTop: '80px' }}>
    <h2
      style={{
        color: 'var(--pixel-text-primary)',
        fontSize: '18px',
        fontFamily: "'Press Start 2P', monospace",
        margin: '0 0 6px',
        borderBottom: '2px solid var(--pixel-border)',
        paddingBottom: '8px',
      }}
    >
      {title}
    </h2>
    {description && (
      <p
        style={{
          color: 'var(--pixel-text-secondary)',
          fontSize: '12px',
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
    )}
  </div>
);

const DesignSystemScreen: React.FC<DesignSystemScreenProps> = ({ onGoHome }) => {
  const [activeSection, setActiveSection] = useState('colors');
  const [currentTheme, setCurrentTheme] = useState(ThemeManager.getCurrentTheme());
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoModalVariant, setDemoModalVariant] = useState<typeof modalVariants[number]>('default');
  const [, forceUpdate] = useState(0);

  const handleThemeChange = useCallback(
    (theme: string) => {
      ThemeManager.applyTheme(theme);
      setCurrentTheme(theme);
      // Force re-render to update color swatches
      forceUpdate(prev => prev + 1);
    },
    []
  );

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const allIconNames = Object.keys(pixelIcons) as PixelIconName[];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--pixel-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sticky Header */}
      <motion.div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'var(--pixel-bg-primary)',
          borderBottom: '2px solid var(--pixel-border)',
          padding: '12px 20px',
          paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <GameButton onClick={onGoHome} variant="secondary" icon="home" text="Home" size="small" />
          <h1
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '18px',
              fontFamily: "'Press Start 2P', monospace",
              margin: 0,
            }}
          >
            Design System
          </h1>
          <code
            style={{
              color: 'var(--pixel-info)',
              fontSize: '10px',
              marginLeft: 'auto',
              background: 'rgba(255,255,255,0.1)',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
          >
            Theme: {currentTheme}
          </code>
        </div>

        {/* Section Navigation */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '4px',
          }}
        >
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '10px',
                fontFamily: "'Press Start 2P', monospace",
                whiteSpace: 'nowrap',
                background:
                  activeSection === section.id ? 'var(--pixel-primary)' : 'var(--pixel-bg-secondary)',
                color:
                  activeSection === section.id ? '#fff' : 'var(--pixel-text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          padding: '20px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* === COLORS & THEMES === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="colors"
            title="Colors & Themes"
            description="CSS custom properties used across the app. Switch themes to see values change."
          />

          {/* Theme Switcher */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {ThemeManager.getAvailableThemes().map(theme => (
              <GameButton
                key={theme}
                onClick={() => handleThemeChange(theme)}
                variant={currentTheme === theme ? 'primary' : 'secondary'}
                text={theme.charAt(0).toUpperCase() + theme.slice(1)}
                size="small"
              />
            ))}
          </div>

          {/* Color Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '8px',
            }}
          >
            {themeVars.map(v => (
              <ColorSwatch key={v.name} varName={v.name} label={v.label} />
            ))}
          </div>

          {/* Source Reference */}
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/services/themeManager.ts, src/scss/_pixel-design-system.scss
            </code>
          </div>
        </section>

        {/* === TYPOGRAPHY === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="typography"
            title="Typography"
            description="Font styles and text sizes used throughout the game."
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { cls: 'pixel-text pixel-text-title', label: 'Title (24px)', text: 'Take Me Home' },
              { cls: 'pixel-text pixel-text-large', label: 'Large (18px)', text: 'Level Complete!' },
              { cls: 'pixel-text', label: 'Default (14px)', text: 'Ready for another challenge?' },
              { cls: 'pixel-text pixel-text-small', label: 'Small (10px)', text: 'Press START to continue' },
            ].map(item => (
              <div
                key={item.label}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div className={item.cls}>{item.text}</div>
                <code
                  style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px', marginTop: '4px', display: 'block' }}
                >
                  .{item.cls.replace(/ /g, ' .')} - {item.label}
                </code>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/scss/_pixel-design-system.scss (@mixin pixel-text)
            </code>
          </div>
        </section>

        {/* === BUTTONS === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="buttons"
            title="Buttons"
            description="GameButton component variants and sizes. All buttons use the same component."
          />

          {/* Variants */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            Variants
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
            {buttonVariants.map(variant => (
              <GameButton
                key={variant}
                onClick={() => {}}
                variant={variant}
                icon="play"
                text={variant.charAt(0).toUpperCase() + variant.slice(1)}
              />
            ))}
          </div>

          {/* Sizes */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            Sizes
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '24px' }}>
            {buttonSizes.map(size => (
              <GameButton
                key={size}
                onClick={() => {}}
                variant="primary"
                icon="play"
                text={size.charAt(0).toUpperCase() + size.slice(1)}
                size={size}
              />
            ))}
          </div>

          {/* With icons */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            With Icons
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
            {['home', 'settings', 'shop', 'play', 'restart', 'heart'].map(icon => (
              <GameButton key={icon} onClick={() => {}} variant="primary" icon={icon} text={icon} size="small" />
            ))}
          </div>

          {/* Disabled */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            Disabled
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <GameButton onClick={() => {}} variant="primary" text="Disabled" disabled />
            <GameButton onClick={() => {}} variant="danger" text="Disabled" disabled />
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/components/ui/GameButton.tsx, src/scss/_buttons.scss
            </code>
          </div>
        </section>

        {/* === ICONS === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="icons"
            title="Icons"
            description={`${allIconNames.length} SVG pixel icons available via PixelIcon component.`}
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: '8px',
            }}
          >
            {allIconNames.map(name => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 8px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <PixelIcon name={name} size={28} color="var(--pixel-text-primary)" />
                <code
                  style={{
                    color: 'var(--pixel-text-secondary)',
                    fontSize: '8px',
                    textAlign: 'center',
                    wordBreak: 'break-all',
                  }}
                >
                  {name}
                </code>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/game/pixelIcons.ts, src/components/ui/PixelIcon.tsx
            </code>
          </div>
        </section>

        {/* === MODALS === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="modals"
            title="Modals"
            description="Modal component with variants, sizes, tabs, and responsive behavior."
          />

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {modalVariants.map(variant => (
              <GameButton
                key={variant}
                onClick={() => {
                  setDemoModalVariant(variant);
                  setDemoModalOpen(true);
                }}
                variant={variant === 'default' ? 'secondary' : variant as GameButtonProps['variant']}
                text={`${variant} modal`}
                size="small"
              />
            ))}
          </div>

          {/* Demo Modal */}
          <Modal
            isOpen={demoModalOpen}
            onClose={() => setDemoModalOpen(false)}
            title={`${demoModalVariant} Modal`}
            subtitle="This is a demo modal"
            variant={demoModalVariant}
            size="medium"
          >
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--pixel-text-primary)', marginBottom: '16px' }}>
                This is the <strong>{demoModalVariant}</strong> variant of the Modal component.
              </p>
              <GameButton
                onClick={() => setDemoModalOpen(false)}
                variant="primary"
                text="Close"
                icon="close"
              />
            </div>
          </Modal>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/components/ui/Modal.tsx, src/scss/_modal.scss
            </code>
          </div>
        </section>

        {/* === GAME TILES === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="tiles"
            title="Game Tiles"
            description="Obstacle and special tiles used in levels."
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '16px',
            }}
          >
            {(['rock', 'lava', 'stop', 'start', 'end', 'teleporter'] as const).map(type => (
              <div
                key={type}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <ObstacleTile type={type} size={60} />
                <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>{type}</code>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/components/game/GameBoard.tsx (Tile3D), src/components/game/ObstacleTile.tsx
            </code>
          </div>
        </section>

        {/* === CURRENCY === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="currency"
            title="Currency Display"
            description="In-game currency components showing coins, gems, lives."
          />

          <div
            style={{
              padding: '20px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <CurrencyDisplay coins={1250} gems={42} lives={3} streak={7} showStreak />
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {[
                { icon: 'coin' as PixelIconName, label: 'Coins', color: '#f39c12' },
                { icon: 'gem' as PixelIconName, label: 'Gems', color: '#9b59b6' },
                { icon: 'life' as PixelIconName, label: 'Lives', color: '#e74c3c' },
                { icon: 'star' as PixelIconName, label: 'Stars', color: '#f1c40f' },
                { icon: 'fire' as PixelIconName, label: 'Streak', color: '#e67e22' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PixelIcon name={item.icon} size={24} color={item.color} />
                  <span style={{ color: 'var(--pixel-text-primary)', fontSize: '14px', fontFamily: "'Press Start 2P', monospace" }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: src/components/ui/CurrencyDisplay.tsx, src/components/game/HUD.tsx
            </code>
          </div>
        </section>

        {/* === ANIMATIONS === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="animations"
            title="Animations"
            description="Motion/React animation patterns and CSS keyframe animations."
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
            }}
          >
            {/* Spring animation */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <motion.div
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-primary)',
                  borderRadius: '8px',
                  margin: '0 auto 12px',
                }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>Scale Pulse</code>
            </div>

            {/* Float animation */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <motion.div
                className="animate-float"
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-success)',
                  borderRadius: '8px',
                  margin: '0 auto 12px',
                }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>Float (.animate-float)</code>
            </div>

            {/* Gooey hover */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <div
                className="gooey-element"
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-warning)',
                  borderRadius: '8px',
                  margin: '0 auto 12px',
                  cursor: 'pointer',
                }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>Gooey Hover</code>
            </div>

            {/* Rotate animation */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <motion.div
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-danger)',
                  borderRadius: '8px',
                  margin: '0 auto 12px',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>Continuous Rotate</code>
            </div>

            {/* Entrance animation */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <motion.div
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-info)',
                  borderRadius: '8px',
                  margin: '0 auto 12px',
                }}
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>Bounce Y</code>
            </div>

            {/* Sparkle */}
            <div
              style={{
                padding: '20px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
              }}
            >
              <div
                className="animate-sparkle"
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--pixel-player)',
                  borderRadius: '50%',
                  margin: '0 auto 12px',
                }}
              />
              <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>
                Sparkle (.animate-sparkle)
              </code>
            </div>
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '11px' }}>
              Source: motion/react (runtime), src/scss/_pixel-design-system.scss (CSS keyframes)
            </code>
          </div>
        </section>

        {/* === SPACING === */}
        <section style={{ marginBottom: '40px' }}>
          <SectionHeader
            id="spacing"
            title="Spacing & Layout"
            description="Standard spacing scale and breakpoints."
          />

          {/* Spacing Scale */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            Spacing Scale
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
            {[4, 8, 12, 16, 20, 24, 32, 40, 48, 64].map(size => (
              <div key={size} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: size,
                    height: '20px',
                    background: 'var(--pixel-primary)',
                    borderRadius: '4px',
                    flexShrink: 0,
                  }}
                />
                <code style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px', minWidth: '60px' }}>
                  {size}px
                </code>
              </div>
            ))}
          </div>

          {/* Breakpoints */}
          <h3
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '14px',
              fontFamily: "'Press Start 2P', monospace",
              margin: '0 0 12px',
            }}
          >
            Breakpoints
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { label: 'Mobile S', width: '320px', note: 'min target' },
              { label: 'Mobile M', width: '375px', note: 'common mobile' },
              { label: 'Mobile L', width: '480px', note: 'max-width: 480px' },
              { label: 'Tablet', width: '768px', note: 'max-width: 768px' },
              { label: 'Desktop', width: '1024px', note: 'min-width: 768px' },
              { label: 'Desktop L', width: '1200px', note: 'max content width' },
            ].map(bp => (
              <div
                key={bp.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <span
                  style={{
                    color: 'var(--pixel-text-primary)',
                    fontSize: '12px',
                    fontFamily: "'Press Start 2P', monospace",
                    minWidth: '100px',
                  }}
                >
                  {bp.label}
                </span>
                <code style={{ color: 'var(--pixel-info)', fontSize: '11px' }}>{bp.width}</code>
                <span style={{ color: 'var(--pixel-text-secondary)', fontSize: '10px' }}>({bp.note})</span>
              </div>
            ))}
          </div>

          {/* Current viewport */}
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(78, 205, 196, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(78, 205, 196, 0.3)',
            }}
          >
            <code style={{ color: 'var(--pixel-info)', fontSize: '11px' }}>
              Current viewport: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'}
            </code>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DesignSystemScreen;
